import type { FileableFile, UploadFile } from "types/storage.types";
import { RFX_STATUS } from "utils/constants";

import type { Company } from "newui/features/Company/types";

import type { RfxQuotes } from "../Submission/types/quote.type";
import type { RfxReturnableSchedule } from "./returnable-schedule-question.type";

export type RfxLead = {
	id: number;
	email: string;
	fullName: string;
	phoneNumber: string;
	position: string;
};

export interface RfxGuest {
	id: number;
	name: string;
	email: string;
}

export interface RfxCompany extends RfxGuest {
	contact_name: string;
	phone: string;
}

export type RfxApprover = {
	id: number;
	additional_message?: string;
	approverUserId?: number;
	hasApproved?: boolean;
};

export type RfxType = {
	id: number;
	code: string;
	description: string;
	name: string;
	show_quote: boolean;
	sort_order: number;
};

export type RfxProjectStatusInfo = {
	message: string | null;
	reason: string | null;
	restart_date: string | null;
};

export type RfxProjectType = {
	id: number;
	description: string | null;
	displayOrder: number;
	isActive: boolean;
	isSystemDefault: boolean;
	name: string;
	projectCount: number;
	slug: string;
};

export interface RfxProjectLite {
	id: number;
	name: string;
	is_onboarding: boolean;
}

export interface RfxProject extends RfxProjectLite {
	backgroundImagePath: string;
	companyId: number;
	description: string | null;
	extraInfo: string | null;
	isFavorite: boolean;
	location: string;
	locationLatitude: string;
	locationLongitude: string;
	logoPath: string;
	numberSupplierList: number;
	procurerId: number;
	projectType?: RfxProjectType;
	sector: Sector[];
	state: string;
	status: string;
	statusInfomation: RfxProjectStatusInfo;
	summary: string | null;
	uuid: string;
}

export type RfxClientDocument = {
	id: number;
	uuid: string;
	categoryId: number;
	categoryName: string;
	documentPath: string;
	fileName: string;
	fileSize: number;
	fileType: string;
	rfqId: number;
	sort_order: number;
};

export type Rfx = {
	id: number;
	uuid: string;
	addendum_count?: number;
	allow_flexible_quote: boolean;
	clarifications_count?: number;
	client: string;
	comms_count: number;
	companyId: number;
	companyOwner: Company;
	description: string;
	documentationDescription?: string;
	documentationDocument: RfxClientDocument[];
	escopeOfService: string;
	hasDocumentation?: boolean;
	lastUpdated: string;
	location: string;
	locationLatitude: string;
	locationLongitude: string;
	name: string;
	project: RfxProject;
	questionnaire: RfxReturnableSchedule;
	quoteFile: UploadFile;
	quoteInstructions?: string;
	quotes: RfxQuotes;
	quoteType: "form" | "file";
	returnableScheduleFile: UploadFile;
	returnableScheduleType: "form" | "file";
	rfqIdNumber: string;
	rfxApprovers: RfxApprover[];
	rfxDocumentationZipFile: FileableFile;
	rfxLead: RfxLead;
	rfxPublished: string;
	rfxType: RfxType;
	status: RfxStatus;
	submissionDeadline: string;
	submissionTimezone: string;
	supplierInvited: InvitedSuppliers;
	supplierList: SupplierList;
	type: string;
	unreadMessages: number;
	isQuoteTabValid?: boolean;
	isDocumentationTabValid?: boolean;
	isQuestionnaireTabValid?: boolean;
	isSuppliersTabValid?: boolean;
};

export interface DisplayRfx extends Rfx {
	displayName: string;
}

export type RfxKeys = keyof Rfx;

export const rfxKeys: RfxKeys[] = [
	"addendum_count",
	"allow_flexible_quote",
	"client",
	"comms_count",
	"companyId",
	"companyOwner",
	"description",
	"documentationDescription",
	"documentationDocument",
	"escopeOfService",
	"hasDocumentation",
	"id",
	"lastUpdated",
	"location",
	"locationLatitude",
	"locationLongitude",
	"name",
	"project",
	"questionnaire",
	"quoteFile",
	"quoteInstructions",
	"quotes",
	"quoteType",
	"rfxApprovers",
	"returnableScheduleFile",
	"returnableScheduleType",
	"rfqIdNumber",
	"rfxLead",
	"rfxPublished",
	"rfxType",
	"status",
	"submissionDeadline",
	"submissionTimezone",
	"supplierInvited",
	"supplierList",
	"type",
	"uuid",
];

export type RfxStatus = (typeof RFX_STATUS)[keyof typeof RFX_STATUS];

type Sector = {
	id: number;
	created_at: string;
	name: string;
	projects_count: number | null;
	updated_at: string;
};

export type Supplier = {
	id: number;
	size_type_id: number;
	abn: string;
	account_status: string | null;
	banner_path: string;
	company_number: string;
	contact_name: string;
	contract: any | null; // Replace 'any' with specific contract type if known
	documents: any | null; // Replace 'any' with specific documents type if known
	email: string;
	locations: Location[];
	logo_path: string;
	name: string;
	sector: Sector;
	size_name: string;
	type: any | null;
};

export type SupplierList = {
	id: number;
	uuid: string;
	code: string;
	companyId: number;
	description: string;
	endDate: string;
	groupId: number | null;
	is_company_approved_list: boolean;
	is_onboarding: boolean;
	name: string;
	startDate: string;
	status: string;
	suppliers: Supplier[];
	userId: number;
};

export type InvitedSuppliers = {
	accepted: InvitedSupplier[];
	all: InvitedSupplier[];
	declined: InvitedSupplier[];
	submitted: InvitedSupplier[];
};

type InvitedSupplier = {
	company_id: number;
	id: number;
	uuid: string;
	email: string;
	is_guest: boolean;
	lastUpdated: string;
	message: string | null;
	name: string;
	rfqId: number;
	status: string;
	submittedAt: string | null;
};
